// Subscription Management System
// Handles user subscription status and premium features access

import { onAuthStateChange } from './auth.js';
import { db } from './firebase-config.js';
import { collection, query, where, getDocs, doc, getDoc } from 'firebase/firestore';
import { trackPremiumConversion } from './meta-pixel.js';

// Subscription tiers
export const SUBSCRIPTION_TIERS = {
    FREE: 'free',
    PREMIUM: 'premium'
};

// Lemon Squeezy / Firestore values that grant premium access
const PREMIUM_SUBSCRIPTION_STATUSES = new Set(['active', 'paid']);

function hasPremiumSubscriptionStatus(userData) {
    if (!userData) return false;
    const statuses = [
        userData.status,
        userData.subscriptionStatus,
        userData.rawData?.attributes?.status
    ];
    return statuses.some(
        (value) => value != null && PREMIUM_SUBSCRIPTION_STATUSES.has(String(value).toLowerCase())
    );
}

// Free tier limits (non-premium: logged-in or not)
export const FREE_TIER_LIMITS = {
    PLANT_BUTTONS: 7,  // Legacy; plant access uses VEGETABLE_BUTTONS
    VEGETABLE_BUTTONS: 10,  // Only first 10 vegetables accessible for non-premium users
    FEATURES: {
        BED_SIZE_CONTROLS: false,
        FULL_PLANT_LIBRARY: false,
        ADVANCED_FEATURES: false
    }
};

class SubscriptionManager {
    constructor() {
        this.currentUser = null;
        this.userSubscription = SUBSCRIPTION_TIERS.FREE;
        this.subscriptionData = null;
        this.callbacks = [];
        this.isInitialized = false;
        
        this.init();
    }

    init() {
        // Prevent multiple initializations
        if (this.isInitialized) {
            console.warn('⚠️ Subscription Manager already initialized');
            return;
        }
        
        console.log('🎯 Initializing Subscription Manager...');
        
        // Listen for auth state changes
        this.unsubscribeAuth = onAuthStateChange(async (user) => {
            console.log('👤 Auth state changed:', user ? `User logged in: ${user.email}` : 'User logged out');
            
            // Prevent duplicate handling of the same user
            if (this.currentUser?.uid === user?.uid) {
                console.log('🔄 Ignoring duplicate auth state change for:', user?.email);
                return;
            }
            
            this.currentUser = user;
            if (user) {
                // Clear any existing subscription data first
                this.resetSubscription();
                
                // Load fresh subscription data
                await this.loadUserSubscription(user);
                
                console.log('✅ Subscription status updated after login:', {
                    email: user.email,
                    tier: this.userSubscription,
                    isPremium: this.isPremiumUser()
                });
            } else {
                console.log('👋 User logged out, resetting subscription');
                this.resetSubscription();
            }
        });
        
        // Re-check subscription when the user returns to this tab (e.g. after completing checkout
        // in another tab). Only runs for logged-in users who are still on the free tier.
        document.addEventListener('visibilitychange', async () => {
            if (document.visibilityState === 'visible' && this.currentUser && !this.isPremiumUser()) {
                console.log('👁️ Tab visible — re-checking subscription for free-tier user:', this.currentUser.email);
                await this.loadUserSubscription(this.currentUser);
            }
        });

        this.isInitialized = true;
        console.log('✨ Subscription Manager initialized successfully');
    }

    async loadUserSubscription(user) {
        console.log('📥 Loading subscription for user:', user.email);
        
        try {
            // Normalize to lowercase — Lemon Squeezy stores whatever the user typed at checkout
            const normalizedEmail = (user.email || '').toLowerCase();

            // First try to find user by email
            const usersRef = collection(db, 'users');
            const q = query(usersRef, where('email', '==', normalizedEmail));
            const querySnapshot = await getDocs(q);

            console.log('🔍 Looking up user by email:', normalizedEmail);
            
            let userData = null;
            if (!querySnapshot.empty) {
                userData = querySnapshot.docs[0].data();
                console.log('✅ Found user data by email:', {
                    email: user.email,
                    subscriptionStatus: userData.subscriptionStatus,
                    status: userData.status
                });
            } else {
                // Fallback to checking by UID
                console.log('ℹ️ No user found by email, checking by UID:', user.uid);
                const userDocRef = doc(db, 'users', user.uid);
                const userDoc = await getDoc(userDocRef);
                if (userDoc.exists()) {
                    userData = userDoc.data();
                }
            }
            
            // Log the complete user data for debugging
            console.log('📋 Complete user data:', userData);

            // Check for active subscription or lifetime (forever) access
            const isLifetime = userData?.lifetimeAccess === true;
            const isActive = hasPremiumSubscriptionStatus(userData);

            if (isActive || isLifetime) {
                console.log(isLifetime ? '✅ Lifetime access confirmed in Firestore' : '✅ Active subscription confirmed in Firestore');
                this.subscriptionData = {
                    tier: SUBSCRIPTION_TIERS.PREMIUM,
                    lifetimeAccess: isLifetime,
                    startDate: userData.lastUpdated?.toDate()?.toISOString() || new Date().toISOString(),
                    endDate: null,
                    features: {
                        BED_SIZE_CONTROLS: true,
                        FULL_PLANT_LIBRARY: true,
                        ADVANCED_FEATURES: true
                    }
                };
                this.userSubscription = SUBSCRIPTION_TIERS.PREMIUM;
                
                // Store in localStorage as backup
                localStorage.setItem(`subscription_${user.uid}`, JSON.stringify(this.subscriptionData));
                
                trackPremiumConversion({ uid: user.uid, lifetimeAccess: isLifetime });
                
                console.log('💫 Premium features enabled for:', user.email);
            } else {
                console.log('ℹ️ No active subscription found for:', user.email);
                this.resetSubscription();
            }
        } catch (error) {
            console.error('❌ Error loading subscription:', error, error.stack);
            this.resetSubscription();
        }
        
        // Notify all subscribers of the change
        this.notifySubscriptionChange();
        
        // Log final state
        console.log('📊 Final subscription state:', {
            email: user.email,
            tier: this.userSubscription,
            isPremium: this.isPremiumUser(),
            features: this.subscriptionData?.features
        });
    }

    resetSubscription() {
        console.log('🔄 Resetting subscription to free tier');
        this.subscriptionData = {
            tier: SUBSCRIPTION_TIERS.FREE,
            startDate: new Date().toISOString(),
            endDate: null,
            features: FREE_TIER_LIMITS.FEATURES
        };
        this.userSubscription = SUBSCRIPTION_TIERS.FREE;
        this.notifySubscriptionChange();
    }

    // Check if user has premium access
    isPremiumUser() {
        return this.userSubscription === SUBSCRIPTION_TIERS.PREMIUM;
    }

    // Check if user is logged in
    isLoggedIn() {
        return !!this.currentUser;
    }

    // Get accessible plant count for current user (first N vegetables for non-premium)
    getAccessiblePlantCount() {
        if (this.isPremiumUser()) {
            return Infinity; // No limit for premium users
        }
        return FREE_TIER_LIMITS.VEGETABLE_BUTTONS;
    }

    // Check if user can access bed size controls
    canAccessBedSizeControls() {
        if (!this.isLoggedIn()) {
            return false;
        }
        
        return this.isPremiumUser();
    }

    // Check if user can access full plant library
    canAccessFullPlantLibrary() {
        if (!this.isLoggedIn()) {
            return false;
        }
        
        return this.isPremiumUser();
    }

    // Upgrade user to premium (simulate payment)
    async upgradeToPremium() {
        if (!this.currentUser) {
            throw new Error('User must be logged in to upgrade');
        }

        console.log('💳 Upgrading user to premium...');
        
        try {
            // Simulate payment processing
            await this.simulatePaymentProcessing();
            
            // Update subscription data
            const premiumSubscription = {
                tier: SUBSCRIPTION_TIERS.PREMIUM,
                startDate: new Date().toISOString(),
                endDate: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString(), // 1 year
                features: {
                    BED_SIZE_CONTROLS: true,
                    FULL_PLANT_LIBRARY: true,
                    ADVANCED_FEATURES: true
                }
            };
            
            // Store subscription data
            localStorage.setItem(`subscription_${this.currentUser.uid}`, JSON.stringify(premiumSubscription));
            
            // Update current state
            this.subscriptionData = premiumSubscription;
            this.userSubscription = SUBSCRIPTION_TIERS.PREMIUM;
            
            console.log('✅ User upgraded to premium successfully');
            this.notifySubscriptionChange();
            
            return { success: true };
            
        } catch (error) {
            console.error('❌ Error upgrading to premium:', error);
            return { success: false, error: error.message };
        }
    }

    async simulatePaymentProcessing() {
        // Simulate payment processing delay
        return new Promise((resolve) => {
            setTimeout(() => {
                console.log('💳 Payment processed successfully');
                resolve();
            }, 1500);
        });
    }

    // Subscribe to subscription changes
    onSubscriptionChange(callback) {
        this.callbacks.push(callback);
        
        // Call immediately with current state
        callback({
            isLoggedIn: this.isLoggedIn(),
            isPremium: this.isPremiumUser(),
            lifetimeAccess: this.subscriptionData?.lifetimeAccess === true,
            tier: this.userSubscription,
            accessiblePlantCount: this.getAccessiblePlantCount(),
            canAccessBedSizeControls: this.canAccessBedSizeControls(),
            canAccessFullPlantLibrary: this.canAccessFullPlantLibrary()
        });
    }

    notifySubscriptionChange() {
        const subscriptionState = {
            isLoggedIn: this.isLoggedIn(),
            isPremium: this.isPremiumUser(),
            lifetimeAccess: this.subscriptionData?.lifetimeAccess === true,
            tier: this.userSubscription,
            accessiblePlantCount: this.getAccessiblePlantCount(),
            canAccessBedSizeControls: this.canAccessBedSizeControls(),
            canAccessFullPlantLibrary: this.canAccessFullPlantLibrary()
        };
        
        console.log('📢 Notifying subscription change:', subscriptionState);
        
        // Use Promise.resolve().then to ensure callbacks are executed in the next tick
        this.callbacks.forEach(callback => {
            Promise.resolve().then(() => {
                try {
                    callback(subscriptionState);
                } catch (error) {
                    console.error('❌ Error in subscription callback:', error);
                }
            });
        });
    }

    // Get subscription info for display
    getSubscriptionInfo() {
        return {
            tier: this.userSubscription,
            isLoggedIn: this.isLoggedIn(),
            isPremium: this.isPremiumUser(),
            lifetimeAccess: this.subscriptionData?.lifetimeAccess === true,
            data: this.subscriptionData
        };
    }

    // Add cleanup method
    cleanup() {
        if (this.unsubscribeAuth) {
            this.unsubscribeAuth();
        }
        this.callbacks = [];
        this.isInitialized = false;
    }
}

// Create and export singleton instance
export const subscriptionManager = new SubscriptionManager();

// Export initialization function
export const initializeSubscription = () => {
    console.log('🚀 Subscription system initialized');
    
    // Make available globally for debugging
    window.subscriptionManager = subscriptionManager;
    window.SUBSCRIPTION_TIERS = SUBSCRIPTION_TIERS;
    
    // Add test functions for debugging
    window.testSubscription = {
        upgradeToPremium: () => subscriptionManager.upgradeToPremium(),
        getInfo: () => subscriptionManager.getSubscriptionInfo(),
        checkAccess: () => ({
            plants: subscriptionManager.getAccessiblePlantCount(),
            bedControls: subscriptionManager.canAccessBedSizeControls(),
            fullLibrary: subscriptionManager.canAccessFullPlantLibrary()
        })
    };
    
    console.log('💡 Run window.testSubscription.getInfo() to check subscription status');
    console.log('💡 Run window.testSubscription.upgradeToPremium() to test upgrade');
    
    return subscriptionManager;
};
